import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, AlertCircle, MapPin, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import Navigation from '@/components/Navigation';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    if (user) {
      loadRegions(user.currency);
    }
  }, [user]);

  useEffect(() => {
    if (selectedRegion) {
      fetchDashboardData();
    }
  }, [selectedRegion]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      setSelectedRegion(response.data.region || null);
    } catch (error) {
      console.error('Error fetching user:', error);
      toast.error('Failed to load user data');
      setLoading(false);
    }
  };

  const loadRegions = async (currency) => {
    try {
      let endpoint = '';
      if (currency === 'USD') {
        endpoint = '/api/regions/us-states';
      } else if (currency === 'EUR') {
        endpoint = '/api/regions/eu-countries';
      } else if (currency === 'HKD') {
        endpoint = '/api/regions/hkd-regions';
      }
      
      const response = await axios.get(`${BACKEND_URL}${endpoint}`, {
        withCredentials: true
      });
      setRegions(response.data.regions || []);
      
      // Only set default region if user doesn't have one
      if (!selectedRegion && response.data.regions.length > 0) {
        setSelectedRegion(response.data.regions[0].code);
      } else if (!selectedRegion) {
        // If still no region, just fetch dashboard without region update
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading regions:', error);
      toast.error('Failed to load regions');
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Only update user region preference if we have a valid region
      if (selectedRegion && selectedRegion !== 'null' && selectedRegion !== '') {
        try {
          await axios.put(
            `${BACKEND_URL}/api/user/region`,
            { region: selectedRegion },
            { withCredentials: true }
          );
        } catch (regionError) {
          console.warn('Failed to update region preference:', regionError);
          // Don't fail the whole dashboard load if region update fails
        }
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/dashboard/comparison`, {
        withCredentials: true
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !dashboardData) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const officialRate = dashboardData?.official_inflation?.yearly_rate_pct || 0;
  const coreRate = dashboardData?.official_inflation?.core_rate_pct || 0;
  const personalRate = dashboardData?.personal_inflation?.yearly_rate_pct || 0;
  const difference = personalRate - officialRate;
  const hasData = !dashboardData?.personal_inflation?.insufficient_data;
  const monthlyHistory = dashboardData?.monthly_history || [];

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard">
      <Navigation />
      <TooltipProvider>
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="font-heading font-black text-4xl md:text-5xl mb-2">Your Inflation Dashboard</h1>
          <p className="text-muted-foreground mb-8">Compare official vs. personal inflation rates</p>
        </motion.div>

        {/* Region Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mb-8"
        >
          <Card className="bg-card border-card-border">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <MapPin className="w-5 h-5 text-primary" />
                <div className="flex-1">
                  <Label className="mb-2 block">Select Region/State</Label>
                  <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                    <SelectTrigger className="bg-background border-card-border max-w-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-card-border">
                      {regions.map((region) => (
                        <SelectItem key={region.code} value={region.code}>
                          {region.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {!hasData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card className="bg-accent/20 border-accent mb-8">
              <CardContent className="p-6 flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold mb-2">Not Enough Data Yet</h3>
                  <p className="text-sm text-muted-foreground">
                    Start tracking your expenses to calculate your personal inflation rate. Add at least 6 months of recurring purchases to see accurate results.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="md:col-span-2 md:row-span-2"
          >
            <Card className="bg-card border-card-border h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Your Personal Inflation
                </CardTitle>
                <CardDescription>Based on your actual spending patterns</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-6xl font-heading font-black mb-4 text-primary">
                  {personalRate.toFixed(1)}%
                </div>
                <div className="flex items-center gap-2 mb-6">
                  {difference > 0 ? (
                    <Badge className="bg-destructive text-white">
                      {difference.toFixed(1)}% higher than official
                    </Badge>
                  ) : difference < 0 ? (
                    <Badge className="bg-success text-white">
                      {Math.abs(difference).toFixed(1)}% lower than official
                    </Badge>
                  ) : (
                    <Badge>Same as official rate</Badge>
                  )}
                </div>
                {hasData && dashboardData?.personal_inflation?.sample_items?.length > 0 && (
                  <div>
                    <h4 className="font-bold mb-3 text-sm">Top Price Changes:</h4>
                    <div className="space-y-2">
                      {dashboardData.personal_inflation.sample_items.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">{item.item}</span>
                          <span className={item.change_pct > 0 ? 'text-destructive font-bold' : 'text-success font-bold'}>
                            {item.change_pct > 0 ? '+' : ''}{item.change_pct.toFixed(1)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <Card className="bg-card border-card-border">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  Official Rate
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs bg-card border-card-border">
                      <p className="font-bold mb-1">Headline CPI (Consumer Price Index)</p>
                      <p className="text-sm text-muted-foreground">
                        Measures the average change in prices for ALL goods and services, including volatile items like food and energy. This is the "official" inflation rate you see in the news.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </CardTitle>
                <CardDescription className="text-xs">{dashboardData?.region_name || 'Region'}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-heading font-black text-secondary">
                  {officialRate.toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Headline CPI - {dashboardData?.official_inflation?.period || 'Current'}
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
          >
            <Card className="bg-card border-card-border">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  Core Rate
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs bg-card border-card-border">
                      <p className="font-bold mb-1">Core CPI (Core Consumer Price Index)</p>
                      <p className="text-sm text-muted-foreground">
                        Excludes food and energy prices, which can swing wildly. Economists prefer this to see the "true" underlying inflation trend without short-term noise.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </CardTitle>
                <CardDescription className="text-xs">Excluding food & energy</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-heading font-black text-accent">
                  {coreRate.toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Core CPI
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Monthly Chart */}
        {monthlyHistory.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
          >
            <Card className="bg-card border-card-border">
              <CardHeader>
                <CardTitle>Monthly Inflation Trend</CardTitle>
                <CardDescription>Last 12 months for {dashboardData?.region_name || 'selected region'}</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyHistory}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                    <XAxis 
                      dataKey="month" 
                      stroke="#A1A1AA"
                      tick={{ fill: '#A1A1AA', fontSize: 12 }}
                    />
                    <YAxis 
                      stroke="#A1A1AA"
                      tick={{ fill: '#A1A1AA', fontSize: 12 }}
                      label={{ value: 'Rate (%)', angle: -90, position: 'insideLeft', fill: '#A1A1AA' }}
                    />
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: '#0A0A0A', 
                        border: '1px solid #262626',
                        borderRadius: '8px',
                        color: '#FAFAFA'
                      }}
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-card border border-card-border rounded-lg p-3">
                              <p className="font-bold mb-2">{label}</p>
                              {payload.map((entry, index) => (
                                <div key={index} className="text-sm mb-1">
                                  <span style={{ color: entry.color }} className="font-bold">
                                    {entry.name}: {entry.value}%
                                  </span>
                                  {entry.name === 'Headline CPI' && (
                                    <p className="text-xs text-muted-foreground mt-1">Includes all goods & services</p>
                                  )}
                                  {entry.name === 'Core CPI' && (
                                    <p className="text-xs text-muted-foreground mt-1">Excludes food & energy</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ color: '#FAFAFA' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="rate_pct" 
                      stroke="#06B6D4" 
                      strokeWidth={2}
                      name="Headline CPI"
                      dot={{ fill: '#06B6D4', r: 4 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="core_rate_pct" 
                      stroke="#F59E0B" 
                      strokeWidth={2}
                      name="Core CPI"
                      dot={{ fill: '#F59E0B', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
      </TooltipProvider>
    </div>
  );
};

export default Dashboard;
